<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

// InteliChat (Nextlevel): the InteliChat chrome is PERSISTENT - it wraps
// EVERY route (native settings/reports/contacts/notifications included),
// so an agent never sees the raw native shell. Classic (native) chrome
// stays reachable via the explicit ?classic=1 bypass / the Classic-view
// nav entry, and stays sticky while navigating native surfaces.
import InteliSidebar from 'dashboard/intelichat/shell/InteliSidebar.vue';
import InteliTopbar from 'dashboard/intelichat/shell/InteliTopbar.vue';
import InteliSearch from 'dashboard/intelichat/shell/InteliSearch.vue';
// Phone (<768px) navigation: the icon rail is hidden below the phone
// breakpoint and this bottom tab bar (+ "More" sheet) takes over -
// charlie's hybrid mobile spec. Renders nothing at >=768px.
import InteliMobileNav from 'dashboard/intelichat/shell/InteliMobileNav.vue';
// InteliChat #41 never-miss: always-visible Intel Center HUD + new-chat toast.
import IntelCenterHud from 'dashboard/intelichat/alerts/IntelCenterHud.vue';
import NewChatToast from 'dashboard/intelichat/alerts/NewChatToast.vue';
// Design tokens + fonts: imported as a real module (not a style-block
// @import) so the relative font URLs resolve against styles/ at build.
import 'dashboard/intelichat/styles/tokens.scss';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    InteliSidebar,
    InteliTopbar,
    InteliSearch,
    InteliMobileNav,
    IntelCenterHud,
    NewChatToast,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      // Classic (native) chrome mode. Entered ONLY via the explicit
      // ?classic=1 bypass; sticky across follow-up native navigations
      // (native links drop the query); exits the moment an InteliChat
      // route is opened. Default is ALWAYS the InteliChat chrome.
      classicChrome: false,
    };
  },
  computed: {
    // InteliChat (Nextlevel): persistent chrome - InteliChat owns the
    // shell on every route unless the agent explicitly went Classic.
    useInteliChrome() {
      return !this.classicChrome;
    },
    isInteliRoute() {
      return Boolean(this.$route.meta?.hideNativeChrome);
    },
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    $route: {
      handler(to) {
        if (to?.meta?.hideNativeChrome) {
          // any InteliChat route ends Classic mode
          this.classicChrome = false;
        } else if (to?.query?.classic) {
          this.classicChrome = true;
        }
      },
      immediate: true,
    },
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div
    class="flex-grow overflow-hidden text-n-slate-12"
    :class="useInteliChrome ? 'il-app il-app--host' : 'flex'"
  >
    <NextSidebar
      v-if="!useInteliChrome"
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />
    <InteliSidebar v-if="useInteliChrome" />

    <div class="il-main flex flex-col flex-1 h-full min-w-0 overflow-hidden">
      <InteliTopbar v-if="useInteliChrome" />
      <main
        class="flex flex-1 w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
      >
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        >
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
        </UpgradePage>
        <template v-if="!showUpgradePage">
          <router-view
            :class="{ 'il-native-page': useInteliChrome && !isInteliRoute }"
          />
          <CommandBar />
          <CopilotLauncher />
          <MobileSidebarLauncher
            v-if="!useInteliChrome"
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
          <CopilotContainer />
          <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
        </template>
        <AddAccountModal
          :show="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
      </main>
    </div>

    <InteliMobileNav v-if="useInteliChrome" />
    <InteliSearch v-if="useInteliChrome" />
    <IntelCenterHud v-if="useInteliChrome" />
    <NewChatToast v-if="useInteliChrome" />
  </div>
</template>

<style lang="scss">
/* InteliChat persistent chrome layout (moved here from InteliChatShell.vue
   when the chrome became app-wide). Unscoped on purpose; every selector is
   namespaced under .il-app, so nothing leaks into Classic-mode rendering. */
.il-app {
  display: grid;
  grid-template-columns: 232px 1fr;
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  .il-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }

  .il-page {
    flex: 1;
    /* IC #43: the workspace content region scrolls its OWN overflow
       (overflow-y:auto + min-height:0 defeats the flexbox min-height
       trap); the OUTER app frame (.il-app/.il-main/main) stays fixed,
       so this does NOT reintroduce the #27 body-scroll/section-shift.
       overscroll-behavior:contain stops scroll-chaining to the frame. */
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    min-height: 0;
    min-width: 0;
    position: relative;
  }
}

.il-app.il-app--host {
  /* the AppContainer root is a flex item of the app column (banners may
     sit above it); flex-grow sizes it, so the shell-era height:100%
     must not fight the flex algorithm. */
  height: auto;
  min-height: 0;
}

/* TABLET band only (768-900): compact icon rail. Native pages keep
   their natural minimum and the content area may h-scroll if squeezed. */
@media (min-width: 768px) and (max-width: 900px) {
  .il-app {
    grid-template-columns: 64px 1fr;
  }

  .il-app .il-main > main {
    overflow-x: auto;
  }

  .il-app .il-main > main > .il-native-page {
    min-width: 390px;
  }
}

/* PHONE (<768px, charlie's mobile spec section 2): the left icon rail
   is HIDDEN and the InteliMobileNav bottom tab bar takes over as a
   normal grid row (content is never covered; safe-area handled inside
   the nav). Content reclaims the full viewport width, which also
   removes the old native-admin-page 390px horizontal scroll: native
   pages now render full-width. */
@media (max-width: 767.98px) {
  .il-app {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .il-app .il-side {
    display: none;
  }

  .il-app .il-main > main > .il-native-page {
    min-width: 0;
    width: 100%;
  }

  /* fail-safe only: content that genuinely cannot shrink scrolls inside
     the content area instead of blowing the page layout out. */
  .il-app .il-main > main {
    overflow-x: auto;
  }
}
</style>
